import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const PatientDosage = () => {
// State variables for dosage calculation
const [patientWeight, setPatientWeight] = useState(0); // Patient's weight (kg)
const [dosagePerKg, setDosagePerKg] = useState(0); // Dosage per kg (mg/kg)
const [bodySurfaceArea, setBodySurfaceArea] = useState(0); // Patient's body surface area (m²)
const [dosagePerM2, setDosagePerM2] = useState(0); // Dosage per m² (mg/m²)
const [calculatedDosage, setCalculatedDosage] = useState(0); // Calculated medication dosage
const [calculationMethod, setCalculationMethod] = useState('weight'); // Calculation method: 'weight' or 'bsa'

// Event handlers to update state values
const handleWeightChange = (e) => setPatientWeight(parseFloat(e.target.value) || 0);
const handleDosagePerKgChange = (e) => setDosagePerKg(parseFloat(e.target.value) || 0);
const handleBodySurfaceAreaChange = (e) => setBodySurfaceArea(parseFloat(e.target.value) || 0);
const handleDosagePerM2Change = (e) => setDosagePerM2(parseFloat(e.target.value) || 0);
const handleCalculationMethodChange = (e) => setCalculationMethod(e.target.value);

// useEffect hook to calculate medication dosage
useEffect(() => {
  if (calculationMethod === 'weight' && patientWeight > 0 && dosagePerKg > 0) {
    // Dosage based on weight (mg/kg)
    const dosageByWeight = patientWeight * dosagePerKg;
    setCalculatedDosage(dosageByWeight);
  } else if (calculationMethod === 'bsa' && bodySurfaceArea > 0 && dosagePerM2 > 0) {
    // Dosage based on body surface area (mg/m²)
    const dosageByBSA = bodySurfaceArea * dosagePerM2;
    setCalculatedDosage(dosageByBSA);
  } else {
    setCalculatedDosage(0);
  }

  // This will initialize or reinitialize all popovers on the page whenever the component renders
  const popoverTriggerList = [].slice.call(
    document.querySelectorAll('[data-bs-toggle="popover"]')
  );

  // Clean up any existing popover instances
  popoverTriggerList.forEach((popoverTriggerEl) => {
    const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
    if (popover) {
      popover.dispose(); // Dispose of existing popover
    }
  });

  // Re-initialize popovers
  popoverTriggerList.forEach((popoverTriggerEl) => {
    new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
  });

}, [patientWeight, dosagePerKg, bodySurfaceArea, dosagePerM2, calculationMethod]);

  return (
    <Layout title="Patient Dosage Calculator" path="patient-dosage">
      <section className="section-padding" id="patient-dosage-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Dosage Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Patient Dosage Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Selection for Calculation Method */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="calculationMethod" className="form-label">
                        Calculation Method
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Calculation Method:</strong> Choose how the dosage should be calculated, either based on weight (kg) or body surface area (m²)."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <select
                        id="calculationMethod"
                        className="form-select"
                        value={calculationMethod}
                        onChange={handleCalculationMethodChange}
                      >
                        <option value="weight">Based on Weight (kg)</option>
                        <option value="bsa">Based on Body Surface Area (m²)</option>
                      </select>
                    </div>

                    {/* Input for Patient Weight and Dosage per kg (for Weight-based Calculation) */}
                    {calculationMethod === 'weight' && (
                      <>
                        <div className="col-md-6 col-lg-4">
                          <label htmlFor="patientWeight" className="form-label">
                            Patient Weight (kg)
                            <span
                              className="ms-2 text-muted"
                              data-bs-toggle="popover"
                              data-bs-html="true"
                              data-bs-placement="top"
                              data-bs-content="<strong>Patient Weight:</strong> The weight of the patient in kilograms (kg)."
                              style={{ cursor: "pointer" }}
                            >
                              <i className="bi bi-info-circle"></i>
                            </span>
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            id="patientWeight"
                            placeholder="Enter patient's weight"
                            value={patientWeight || ""}
                            onChange={handleWeightChange}
                          />
                        </div>

                        <div className="col-md-6 col-lg-4">
                          <label htmlFor="dosagePerKg" className="form-label">
                            Dosage per kg (mg/kg)
                            <span
                              className="ms-2 text-muted"
                              data-bs-toggle="popover"
                              data-bs-html="true"
                              data-bs-placement="top"
                              data-bs-content="<strong>Dosage per kg:</strong> The medication dosage in milligrams (mg) per kilogram (kg) of the patient's body weight."
                              style={{ cursor: "pointer" }}
                            >
                              <i className="bi bi-info-circle"></i>
                            </span>
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            id="dosagePerKg"
                            placeholder="Enter dosage per kg"
                            value={dosagePerKg || ""}
                            onChange={handleDosagePerKgChange}
                          />
                        </div>
                      </>
                    )}

                    {/* Input for Body Surface Area and Dosage per m² (for BSA-based Calculation) */}
                    {calculationMethod === 'bsa' && (
                      <>
                        <div className="col-md-6 col-lg-4">
                          <label htmlFor="bodySurfaceArea" className="form-label">
                            Body Surface Area (m²)
                            <span
                              className="ms-2 text-muted"
                              data-bs-toggle="popover"
                              data-bs-html="true"
                              data-bs-placement="top"
                              data-bs-content="<strong>Body Surface Area:</strong> The surface area of the patient's body, usually calculated based on weight and height."
                              style={{ cursor: "pointer" }}
                            >
                              <i className="bi bi-info-circle"></i>
                            </span>
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            id="bodySurfaceArea"
                            placeholder="Enter body surface area"
                            value={bodySurfaceArea || ""}
                            onChange={handleBodySurfaceAreaChange}
                          />
                        </div>

                        <div className="col-md-6 col-lg-4">
                          <label htmlFor="dosagePerM2" className="form-label">
                            Dosage per m² (mg/m²)
                            <span
                              className="ms-2 text-muted"
                              data-bs-toggle="popover"
                              data-bs-html="true"
                              data-bs-placement="top"
                              data-bs-content="<strong>Dosage per m²:</strong> The medication dosage in milligrams (mg) per square meter (m²) of the patient's body surface area."
                              style={{ cursor: "pointer" }}
                            >
                              <i className="bi bi-info-circle"></i>
                            </span>
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            id="dosagePerM2"
                            placeholder="Enter dosage per m²"
                            value={dosagePerM2 || ""}
                            onChange={handleDosagePerM2Change}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Calculated Dosage:{" "}
                        <span className="fw-bold ms-3">
                          {calculatedDosage === 0 ? "0.00" : calculatedDosage.toFixed(2)} mg
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Patient Dosage Calculator
                </h3>

                {/* What is a Patient Dosage Calculator? */}
                <h4 className="mt-3 mb-3">What is a Patient Dosage Calculator?</h4>
                <p>
                  The **Patient Dosage Calculator** calculates the accurate medication dosage for patients based on their weight (in kg) or body surface area (BSA, in m²). You can choose between two methods of calculation:
                </p>
                <ul>
                  <li><strong>Weight-based calculation:</strong> Enter the patient's weight in kg and the dosage per kg in mg/kg.</li>
                  <li><strong>Body Surface Area-based calculation:</strong> Enter the patient's body surface area in m² and the dosage per m² in mg/m².</li>
                </ul>

                {/* Why Use a Patient Dosage Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Patient Dosage Calculator?</h4>
                <p>
                  This tool is essential for healthcare providers to ensure accurate medication dosages. It helps prevent the risks of overdosing or underdosing, which can have serious consequences.
                </p>

                {/* Benefits of Using a Patient Dosage Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Patient Dosage Calculator</h4>
                <ul>
                  <li><strong>Ensures Safe and Accurate Medication Administration:</strong> Provides precise dosage calculations in either mg/kg or mg/m² based on patient data.</li>
                  <li><strong>Reduces the Risk of Overdosing or Underdosing:</strong> Minimizes medication errors and ensures proper care.</li>
                  <li><strong>Essential for Personalized Patient Care:</strong> Adjusts the dosage according to patient-specific characteristics like weight or body surface area.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Patient Dosage Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Hospitals:</strong> Ensures accurate dosages for patients based on their physical attributes.</li>
                  <li><strong>Healthcare Providers:</strong> Assists doctors and nurses in calculating medication doses accurately.</li>
                  <li><strong>Pharmacies:</strong> Supports pharmacists in providing the correct dosage for prescriptions.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PatientDosage;
